export interface ApiError {
  message: string;
  code?: string;
  status?: number;
}

export interface ApiResponse<T> {
  data?: T;
  error?: ApiError;
}

const TOKEN_KEY = 'dispatchdash_token';
const USER_CACHE_KEY = 'dispatchdash_user';
/** Prevent hung fetches from blocking auth bootstrap after offline→online. */
export const REQUEST_TIMEOUT_MS = 15_000;

export function isNetworkError(err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status === 0) return true;
  if (status != null && status > 0) return false;
  const message = (err as ApiError | Error | undefined)?.message ?? '';
  return /network|timed out|timeout|failed to fetch|load failed|aborted/i.test(message);
}

/** True when a failed API call should be queued for offline sync instead of surfacing an error. */
export function shouldQueueOffline(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true;
  return isNetworkError(err);
}

let memoryToken: string | null = null;

export function getToken(): string | null {
  return memoryToken ?? localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  memoryToken = token;
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export function clearToken(): void {
  memoryToken = null;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_CACHE_KEY);
}

export function cacheUser(user: User | null): void {
  if (user) {
    localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(USER_CACHE_KEY);
  }
}

export function getCachedUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

type RequestOptions = {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  skipAuth?: boolean;
};

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, headers = {}, skipAuth = false } = options;

  const reqHeaders: Record<string, string> = {
  ...headers,
  };

  if (body !== undefined) {
    reqHeaders['Content-Type'] = 'application/json';
  }

  const token = getToken();
  if (!skipAuth && token) {
    reqHeaders['Authorization'] = `Bearer ${token}`;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      headers: reqHeaders,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'include',
      signal: controller.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw { message: 'Request timed out', status: 0 } as ApiError;
    }
    throw { message: 'Network error', status: 0 } as ApiError;
  } finally {
    clearTimeout(timeoutId);
  }

  // Credential checks (wrong password at sign-in or when changing it) are not
  // session expiry: keep the session and surface a readable message.
  if (response.status === 401 && (path === '/auth/login' || path === '/auth/change-password')) {
    throw {
      message:
        path === '/auth/login'
          ? 'Incorrect email or password.'
          : 'Your current password is incorrect.',
      status: 401,
    } as ApiError;
  }

  if (response.status === 401) {
    clearToken();
    if (!path.startsWith('/auth/')) {
      window.location.href = '/login';
    }
    throw { message: 'Your session has expired. Please sign in again.', status: 401 } as ApiError;
  }

  const contentType = response.headers.get('content-type');
  const isJson = contentType?.includes('application/json');

  if (!response.ok) {
    let errorMessage = `Request failed (${response.status})`;
    if (isJson) {
      try {
        const errBody = await response.json();
        errorMessage = errBody.message ?? errBody.error ?? errorMessage;
      } catch {
        /* ignore */
      }
    }
    throw { message: errorMessage, status: response.status } as ApiError;
  }

  if (response.status === 204 || !isJson) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => apiRequest<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
};

// Auth endpoints
export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  personId?: string;
  clientId?: string;
  permissions?: Record<string, boolean>;
  salesAgentId?: string;
  salesEngagement?: 'full_time' | 'part_time' | 'commission_only' | null;
  /** Set when the account still uses a temporary password (invite or admin reset). */
  forcePasswordReset?: boolean;
}

export interface LoginResponse {
  token: string;
  user: User;
}

export interface SetupStatus {
  needsSetup: boolean;
  hasUsers: boolean;
}

export const authApi = {
  login: async (email: string, password: string) => {
    const raw = await api.post<{
      token: string;
      userId?: string;
      id?: string;
      email: string;
      name?: string;
      role?: string;
      personId?: string | null;
      clientId?: string | null;
      permissions?: Record<string, boolean>;
      salesAgentId?: string | null;
      salesEngagement?: 'full_time' | 'part_time' | 'commission_only' | null;
      forcePasswordReset?: boolean;
    }>('/auth/login', { email, password });
    const user = {
      id: raw.id ?? raw.userId ?? '',
      email: raw.email,
      name: raw.name ?? raw.email,
      role: raw.role ?? 'Admin',
      personId: raw.personId ?? undefined,
      clientId: raw.clientId ?? undefined,
      permissions: raw.permissions ?? {},
      salesAgentId: raw.salesAgentId ?? undefined,
      salesEngagement: raw.salesEngagement ?? null,
      forcePasswordReset: Boolean(raw.forcePasswordReset),
    } satisfies User;
    return { token: raw.token, user };
  },
  logout: () => apiRequest('/auth/logout', { method: 'POST', skipAuth: true }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ ok: true }>('/auth/change-password', { currentPassword, newPassword }),
  /** Public: emails a single-use reset link. Same response whether or not the email has an account. */
  requestReset: (email: string) =>
    apiRequest<{ ok: true; message: string }>('/auth/request-reset', {
      method: 'POST',
      body: { email },
      skipAuth: true,
    }),
  /** Public: completes a reset with the emailed token. Bad/expired tokens return 400 with a readable message. */
  resetPassword: (token: string, newPassword: string) =>
    apiRequest<{ ok: true }>('/auth/reset-password', {
      method: 'POST',
      body: { token, newPassword },
      skipAuth: true,
    }),
  me: async () => {
    const raw = await api.get<{
      id: string;
      email: string;
      name?: string;
      role?: string;
      personId?: string | null;
      clientId?: string | null;
      permissions?: Record<string, boolean>;
      salesAgentId?: string | null;
      salesEngagement?: 'full_time' | 'part_time' | 'commission_only' | null;
      forcePasswordReset?: boolean;
    }>('/auth/me');
    return {
      id: raw.id,
      email: raw.email,
      name: raw.name ?? raw.email,
      role: raw.role ?? 'Admin',
      personId: raw.personId ?? undefined,
      clientId: raw.clientId ?? undefined,
      permissions: raw.permissions ?? {},
      salesAgentId: raw.salesAgentId ?? undefined,
      salesEngagement: raw.salesEngagement ?? null,
      forcePasswordReset: Boolean(raw.forcePasswordReset),
    } satisfies User;
  },
  setupStatus: () =>
    apiRequest<SetupStatus>('/auth/setup-status', { skipAuth: true }),
  setupAdmin: async (data: { name: string; email: string; password: string }) => {
    const raw = await api.post<{
      token: string;
      user?: { userId: string; email: string; permissions?: Record<string, boolean> };
    }>('/auth/setup', { fullName: data.name, email: data.email, password: data.password });
    return {
      token: raw.token,
      user: {
        id: raw.user?.userId ?? '',
        email: raw.user?.email ?? data.email,
        name: data.name,
        role: 'Admin',
      } satisfies User,
    };
  },
};

// Trip types
export interface Trip {
  id: string;
  reference: string;
  status: string;
  clientName?: string;
  clientId?: string;
  origin?: string;
  destination?: string;
  scheduledDate?: string;
  vehicleId?: string;
  vehiclePlate?: string;
  /** Vehicle run the trip is queued on (trip detail only). */
  vehicleRun?: {
    id: string;
    runNumber: number;
    status: string;
    serviceDate: string;
    sequence: number;
  } | null;
  /** Booked vehicle type (GET /trips and /trips/:id return the trip row's vehicle_type_id). */
  vehicleTypeId?: string | null;
  driverName?: string;
  totalCharges?: string;
  createdAt: string;
  updatedAt?: string;
  stops?: Stop[];
  cargoSummary?: string;
  nextAction?: string;
  timePressure?: 'urgent' | 'warning' | 'normal';
  rtbApprovalState?: 'not_required' | 'pending' | 'approved';
  rtbRequestedAt?: string | null;
  rtbApprovedAt?: string | null;
  tripNumber?: string;
  offMap?: boolean;
  quoteRequired?: boolean;
  quoteAccepted?: boolean;
  quotedAmount?: string | null;
  finalAmount?: string | null;
  manualPrice?: string | null;
  manualPriceBasis?: string | null;
  manualPriceApprovedBy?: string | null;
  pseudoZoneId?: string | null;
  readyToBillGates?: Record<string, boolean>;
  parentTripId?: string | null;
  templateId?: string | null;
  scanningEnabled?: boolean;
}

export interface Stop {
  id: string;
  tripId: string;
  sequence: number;
  type: string;
  status: string;
  address: string;
  contactName?: string;
  contactPhone?: string;
  scheduledTime?: string;
  arrivedAt?: string;
  departedAt?: string;
  hasException?: boolean;
  podId?: string | null;
  /** Cargo check for stops without a manifest; null when the stop does not need one. */
  cargoCheck?: CargoCheck | null;
}

/** Per-stop cargo check (no-manifest stops). See docs/CREW_API_CONTRACT.md §14. */
export interface CargoCheck {
  required: boolean;
  mode: 'piece_count' | 'pic_confirmation';
  expectedPieces: number | null;
  completed: boolean;
  countedPieces: number | null;
  picName: string | null;
  mismatch: boolean;
  mismatchReason: 'short' | 'extra' | 'damaged' | 'other' | null;
  confirmedAt: string | null;
  exceptionId?: string | null;
}

export interface CargoCheckInput {
  mode: 'piece_count' | 'pic_confirmation';
  countedPieces?: number;
  picName: string;
  picConfirmed?: boolean;
  mismatchReason?: 'short' | 'extra' | 'damaged' | 'other';
  note?: string;
  /** JPEG data URL (evidence photo, optional). */
  photo?: string;
}

export interface AttentionCounts {
  unassigned: number;
  awaitingApproval: number;
  overdue: number;
  onHold: number;
  readyToDispatch: number;
}

export interface TripStopInput {
  sequence: number;
  type: 'pickup' | 'drop' | 'return_to_base';
  addressBookEntryId?: string;
  contactPerson?: string;
  contactNumber?: string;
  zoneId?: string;
  latitude?: number;
  longitude?: number;
}

export interface CreateTripPayload {
  clientId: string;
  scheduledPickupAt: string;
  vehicleTypeId: string;
  stops: TripStopInput[];
  cargoDescription?: string;
  declaredPieceCount?: number;
  specialInstructions?: string;
  offMap?: boolean;
  manualPrice?: string;
  manualPriceBasis?: string;
  creditOverride?: boolean;
}

export interface CreatePortalTripPayload {
  scheduledPickupAt: string;
  vehicleTypeId: string;
  stops: TripStopInput[];
  cargoDescription?: string;
  specialInstructions?: string;
}

export interface BookingTripStop {
  id: string;
  tripId: string;
  sequence: number;
  type: string;
  addressBookEntryId?: string | null;
  zoneId?: string | null;
  status: string;
}

export interface BookingChargeLine {
  chargeType: string;
  description: string;
  amount: string;
}

export interface BookingTripResult {
  id: string;
  tripNumber: string;
  clientId: string;
  status: string;
  scheduledPickupAt: string;
  vehicleTypeId: string;
  quotedAmount?: string | null;
  finalAmount?: string | null;
  manualPrice?: string | null;
  quoteRequired?: boolean;
  quoteAccepted?: boolean;
  offMap?: boolean;
  stops?: BookingTripStop[];
  chargeLines?: BookingChargeLine[];
}

export type AssignmentRole = 'driver' | 'helper_1' | 'helper_2' | 'additional_helper';

export interface AssignmentCrewMember {
  personId: string;
  role: AssignmentRole;
}

export interface AssignmentWarning {
  type: string;
  message: string;
  blocks: boolean;
  entityId: string;
}

export interface AssignmentPayload {
  vehicleId: string;
  crew: AssignmentCrewMember[];
}

export interface TripTemplate {
  id: string;
  name: string;
  clientId?: string | null;
  shape: {
    vehicleTypeId: string;
    stops: TripStopInput[];
    cargoDescription?: string;
    specialInstructions?: string;
  };
  createdAt?: string;
}

export interface PortalDocumentStop {
  stopId: string;
  sequence: number;
  stopType: string;
  podId: string | null;
  podNumber: string | null;
  generatedAt: string | null;
  signed: boolean;
}

export interface PortalDocumentIndexRow {
  id: string;
  reference: string;
  scheduledDate: string;
  status: string;
  documents: PortalDocumentStop[];
}

export interface PortalInvoice {
  id: string;
  invoiceNumber: string;
  periodStart: string;
  periodEnd: string;
  periodLabel?: string | null;
  tripCount: number;
  subtotal: string;
  vatAmount: string;
  total: string;
  issuedAt: string;
  paid: boolean;
}

export const portalApi = {
  documentIndex: () => api.get<PortalDocumentIndexRow[]>('/portal/document-index'),
  invoices: () => api.get<PortalInvoice[]>('/portal/invoices'),
};

async function authedFetch(path: string): Promise<Response> {
  const token = getToken();
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
  } catch {
    throw { message: 'Network error — check your connection and try again.', status: 0 } as ApiError;
  }
  if (!res.ok) {
    let message = `Download failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) message = body.message;
    } catch {
      /* not JSON */
    }
    if (res.status === 401) message = 'Your session has expired. Please sign in again.';
    throw { message, status: res.status } as ApiError;
  }
  return res;
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Download a document endpoint that may live in cloud storage (R2). Asks the API for a
 * link first: a presigned bucket URL is opened directly (no CORS needed — the browser
 * navigates to it and the URL carries Content-Disposition: attachment); otherwise the file
 * is streamed through the API with the bearer token and saved from a blob.
 */
export async function downloadDocument(path: string, filename: string): Promise<void> {
  const sep = path.includes('?') ? '&' : '?';
  const linkRes = await authedFetch(`${path}${sep}link=1`);
  const link = (await linkRes.json()) as { mode?: string; url?: string | null; filename?: string };
  if (link.mode === 'redirect' && link.url) {
    const a = document.createElement('a');
    a.href = link.url;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    return;
  }
  const res = await authedFetch(path);
  saveBlob(await res.blob(), link.filename ?? filename);
}

/** Fetch a generated file (e.g. invoice PDF) through the API and save it. */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const res = await authedFetch(path);
  saveBlob(await res.blob(), filename);
}

/**
 * A URL an <img> or new tab can show for a stored image value. Legacy rows hold the image
 * inline as a data: URL; newer rows hold a storage key, resolved to a presigned R2 URL or,
 * for local storage, an authenticated blob URL (caller should revoke blob: URLs).
 */
export async function storedImageUrl(value: string): Promise<string> {
  if (value.startsWith('data:image/')) return value;
  const path = `/storage/object?key=${encodeURIComponent(value)}`;
  const linkRes = await authedFetch(`${path}&link=1`);
  const link = (await linkRes.json()) as { mode?: string; url?: string | null };
  if (link.mode === 'redirect' && link.url) return link.url;
  const res = await authedFetch(path);
  return URL.createObjectURL(await res.blob());
}

/** Open a stored image (receipt, signature, photo) in a new tab. */
export async function openStoredImage(value: string): Promise<void> {
  // Open the tab synchronously (before awaits) so popup blockers allow it.
  const win = window.open('', '_blank');
  try {
    const url = await storedImageUrl(value);
    if (win) {
      if (url.startsWith('data:')) {
        win.document.title = 'Image';
        const img = win.document.createElement('img');
        img.src = url;
        img.style.maxWidth = '100%';
        win.document.body.appendChild(img);
      } else {
        win.location.href = url;
      }
    } else {
      window.location.href = url;
    }
  } catch (err) {
    win?.close();
    throw err;
  }
}

export const bookingApi = {
  createTrip: (data: CreateTripPayload) => api.post<BookingTripResult>('/booking/trips', data),
  createPortalTrip: (data: CreatePortalTripPayload) =>
    api.post<BookingTripResult>('/booking/portal/trips', data),
  cloneTrip: (tripId: string, scheduledPickupAt: string) =>
    api.post<BookingTripResult>(`/booking/trips/${tripId}/clone`, { scheduledPickupAt }),
  saveAsTemplate: (tripId: string, name: string, clientId?: string) =>
    api.post<TripTemplate>(`/booking/trips/${tripId}/template`, { name, clientId }),
  listTemplates: (clientId?: string) =>
    api.get<TripTemplate[]>(`/booking/templates${clientId ? `?clientId=${clientId}` : ''}`),
  createFromTemplate: (templateId: string, scheduledPickupAt: string) =>
    api.post<BookingTripResult>(`/booking/templates/${templateId}/trips`, { scheduledPickupAt }),
  acceptQuote: (tripId: string) => api.post<BookingTripResult>(`/booking/trips/${tripId}/accept-quote`),
  setManualPrice: (tripId: string, data: { manualPrice: string; manualPriceBasis?: string }) =>
    api.post<BookingTripResult>(`/booking/trips/${tripId}/manual-price`, data),
  assignPseudoZone: (tripId: string, zoneId?: string) =>
    api.post<Trip>(`/booking/trips/${tripId}/pseudo-zone`, zoneId ? { zoneId } : {}),
};

export type BulkBookingRowResult = {
  row: number;
  customerReference: string;
  ok: boolean;
  errors: Array<{ row: number; field?: string; message: string }>;
  tripId?: string;
  tripNumber?: string;
  serviceDate?: string;
  vehicleTypeCode?: string;
  routeSummary?: string;
  rateSummary?: string;
  manifestLineCount?: number;
  scanningRequired?: boolean;
};

export type BulkValidateResult = {
  ok: boolean;
  rowCount: number;
  validCount: number;
  errorCount: number;
  rows: BulkBookingRowResult[];
  sourceHash: string;
};

export type BulkSubmitResult = {
  batchId: string;
  status: string;
  createdCount: number;
  rejectedCount: number;
  rows: BulkBookingRowResult[];
  idempotentReplay?: boolean;
  sourceHash?: string;
};

export type BulkBatchSummary = {
  id: string;
  clientId: string;
  status: string;
  sourceFilename: string | null;
  sourceHash: string | null;
  rowCount: number;
  createdCount: number;
  rejectedCount: number;
  createdAt: string;
  updatedAt: string;
};

export type BulkBatchReceipt = BulkBatchSummary & {
  rows: BulkBookingRowResult[];
  idempotencyKey: string;
};

async function downloadCsvPath(path: string, filename: string) {
  const token = getToken();
  const res = await fetch(`/api${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) {
    throw { message: `Download failed (${res.status})`, status: res.status } as ApiError;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const bookingBulkApi = {
  downloadBookingsTemplate: (example = false) =>
    downloadCsvPath(
      `/booking/bulk/templates/bookings.csv${example ? '?example=1' : ''}`,
      'bookings-template.csv',
    ),
  downloadManifestsTemplate: (example = false) =>
    downloadCsvPath(
      `/booking/bulk/templates/manifests.csv${example ? '?example=1' : ''}`,
      'manifests-template.csv',
    ),
  validate: (data: {
    clientId?: string;
    bookingsCsv: string;
    manifestsCsv?: string;
  }) => api.post<BulkValidateResult>('/booking/bulk/validate', data),
  submit: (data: {
    clientId?: string;
    bookingsCsv: string;
    manifestsCsv?: string;
    idempotencyKey: string;
    sourceFilename?: string;
    allOrNothing?: boolean;
  }) => api.post<BulkSubmitResult>('/booking/bulk/submit', data),
  listBatches: (params?: { clientId?: string; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.clientId) qs.set('clientId', params.clientId);
    if (params?.limit) qs.set('limit', String(params.limit));
    const q = qs.toString();
    return api.get<BulkBatchSummary[]>(`/booking/bulk/batches${q ? `?${q}` : ''}`);
  },
  getBatch: (id: string) => api.get<BulkBatchReceipt>(`/booking/bulk/batches/${id}`),
  downloadBatchResults: (id: string) =>
    downloadCsvPath(`/booking/bulk/batches/${id}/results.csv`, `bulk-batch-${id}-results.csv`),
};

export const dispatchApi = {
  validateAssignment: (data: AssignmentPayload) =>
    api.post<AssignmentWarning[]>('/dispatch/validate-assignment', data),
  releaseDispatch: (tripId: string) => api.post<Trip>(`/dispatch/trips/${tripId}/release`),
  board: (params?: { date?: string; vehicleIds?: string[] }) => {
    const qs = new URLSearchParams();
    if (params?.date) qs.set('date', params.date);
    if (params?.vehicleIds?.length) qs.set('vehicleIds', params.vehicleIds.join(','));
    const q = qs.toString();
    return api.get<Trip[]>(`/dispatch/board${q ? `?${q}` : ''}`);
  },
};

export type VehicleRunTripSlot = {
  id: string;
  runId: string;
  tripId: string;
  sequence: number;
  status?: string;
  trip?: Trip & { tripNumber?: string; status: string };
};

export type VehicleRunSummary = {
  id: string;
  runNumber: number;
  status: string;
  version: number;
  tripCount: number;
};

export type VehicleRun = {
  id: string;
  vehicleId: string;
  serviceDate: string;
  /** Run 1, Run 2, ... for this vehicle + Manila day (a truck may run more than once a day). */
  runNumber: number;
  status: string;
  version: number;
  trips: VehicleRunTripSlot[];
  /** GET /vehicle-runs/:vehicleId only: every run of that vehicle + day. */
  runsOfDay?: VehicleRunSummary[];
};

export const vehicleRunsApi = {
  list: (params: { date: string; vehicleIds?: string[] }) => {
    const qs = new URLSearchParams({ date: params.date });
    if (params.vehicleIds?.length) qs.set('vehicleIds', params.vehicleIds.join(','));
    return api.get<VehicleRun[]>(`/vehicle-runs?${qs.toString()}`);
  },
  get: (vehicleId: string, date: string) =>
    api.get<VehicleRun>(`/vehicle-runs/${vehicleId}?date=${encodeURIComponent(date)}`),
  reorder: (runId: string, data: { orderedTripIds: string[]; expectedVersion: number }) =>
    api.post<VehicleRun>(`/vehicle-runs/${runId}/reorder`, data),
  nextTrip: (tripId: string) =>
    api.get<{
      nextTrip: {
        id: string;
        tripNumber: string;
        status: string;
        scheduledPickupAt: string | null;
        sequence: number;
        vehicleId: string | null;
        runId: string;
      } | null;
    }>(`/vehicle-runs/trips/${tripId}/next`),
  forceReturn: (runId: string, data: { reason: string; expectedVersion: number }) =>
    api.post<VehicleRun>(`/vehicle-runs/${runId}/force-return`, data),
  complete: (runId: string, data: { expectedVersion: number }) =>
    api.post<VehicleRun>(`/vehicle-runs/${runId}/complete`, data),
};

export type ExecutionEventType =
  | 'start_trip'
  | 'arrive_stop'
  | 'start_service'
  | 'end_service'
  | 'depart_stop'
  | 'end_trip';

export interface GeoPoint {
  latitude?: number;
  longitude?: number;
  accuracyMetres?: number;
}

/**
 * Client idempotency key + device time for a crew action that may be retried or queued
 * offline (see lib/offlineReplay.ts newCrewActionMeta). Sent as the `Idempotency-Key`
 * header and body `clientEventId`, and body `deviceTimestamp`.
 */
export interface CrewActionOptions {
  clientEventId?: string;
  deviceTimestamp?: string;
}

/** POST for crew mutations: adds the idempotency header/body fields when given. */
export function crewPost<T>(path: string, body: Record<string, unknown>, opts?: CrewActionOptions): Promise<T> {
  const fullBody: Record<string, unknown> = { ...body };
  const headers: Record<string, string> = {};
  if (opts?.clientEventId) {
    fullBody.clientEventId = opts.clientEventId;
    headers['Idempotency-Key'] = opts.clientEventId;
  }
  if (opts?.deviceTimestamp && fullBody.deviceTimestamp === undefined) {
    fullBody.deviceTimestamp = opts.deviceTimestamp;
  }
  return apiRequest<T>(path, { method: 'POST', body: fullBody, headers });
}

export const tripsApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return api.get<Trip[]>(`/trips${qs}`);
  },
  get: (id: string) => api.get<Trip>(`/trips/${id}`),
  create: (data: CreateTripPayload) => api.post<BookingTripResult>('/trips', data),
  update: (id: string, data: Partial<Trip>) => api.patch<Trip>(`/trips/${id}`, data),
  attentionCounts: () => api.get<AttentionCounts>('/trips/attention-counts'),
  approve: (id: string, assignment: AssignmentPayload) =>
    api.post<{ tripId: string; warnings: AssignmentWarning[] }>(`/trips/${id}/approve`, assignment),
  release: (id: string) => api.post<Trip>(`/trips/${id}/release`),
  decline: (id: string, reason?: string) => api.post(`/trips/${id}/decline`, { reason }),
  cancel: (id: string, reason?: string) => api.post(`/trips/${id}/cancel`, { reason }),
  cancellationQuote: (id: string) =>
    api.get<{
      tripId: string;
      feeAmount: string;
      chargePct: number;
      hoursNotice: number | null;
      baseAmount: string | null;
      bandDescription: string;
      afterScheduledPickup: boolean;
    }>(`/trips/${id}/cancellation-quote`),
  fail: (id: string, reason?: string) => api.post(`/trips/${id}/fail`, { reason }),
  myCurrent: () => api.get<Trip>('/trips/my-current'),
  startTrip: (tripId: string, geo?: GeoPoint, opts?: CrewActionOptions) =>
    crewPost(`/trips/${tripId}/start`, { ...geo }, opts),
  endTrip: (tripId: string, geo?: GeoPoint, opts?: CrewActionOptions) =>
    crewPost(`/trips/${tripId}/end`, { ...geo }, opts),
  advanceStop: (
    tripId: string,
    stopId: string,
    eventType: ExecutionEventType,
    geo?: GeoPoint,
    opts?: CrewActionOptions,
  ) => crewPost(`/trips/${tripId}/stops/${stopId}/advance`, { eventType, ...geo }, opts),
  confirmCargo: (tripId: string, stopId: string, body: CargoCheckInput, opts?: CrewActionOptions) =>
    crewPost<{ cargoCheck: CargoCheck; exceptionId?: string }>(
      `/trips/${tripId}/stops/${stopId}/cargo-check`,
      { ...body },
      opts,
    ),
  requestRtb: (tripId: string) => api.post<Trip>(`/execution/trips/${tripId}/rtb/request`),
  approveRtb: (tripId: string) => api.post<Trip>(`/execution/trips/${tripId}/rtb/approve`),
};

export interface AttendanceStatus {
  clockedIn: boolean;
  /** Informational: the current clock-in was logged within the hub radius (null = no location). */
  hubGeofenceMatch: boolean | null;
  hubDistanceM?: number | null;
  /** Location status of the latest clock in/out: ok | unavailable | denied (null on old records). */
  locationStatus?: string | null;
  recordedAt: string | null;
}

/** Clock in/out body. Location is optional — clocking in/out is never blocked by it. */
export interface ClockBody {
  latitude?: number;
  longitude?: number;
  accuracyM?: number;
  locationStatus: 'ok' | 'unavailable' | 'denied';
  device?: string;
}

export interface AttendanceLogPunch {
  id: string;
  recordedAt: string;
  latitude: number | null;
  longitude: number | null;
  accuracyM: number | null;
  locationStatus: string | null;
  hubDistanceM: number | null;
  hubGeofenceMatch: boolean | null;
  device: string | null;
}

export interface AttendanceLogRow {
  key: string;
  personId: string;
  personName: string;
  date: string;
  clockIn: AttendanceLogPunch | null;
  clockOut: AttendanceLogPunch | null;
}

export interface AttendanceLog {
  from: string;
  to: string;
  hub: { latitude: number; longitude: number; radiusM: number | null } | null;
  rows: AttendanceLogRow[];
}

export const attendanceApi = {
  me: () => api.get<AttendanceStatus>('/execution/attendance/me'),
  clockIn: (body: ClockBody, opts?: CrewActionOptions) =>
    crewPost<{ id: string; hubGeofenceMatch: boolean | null }>('/execution/attendance/clock-in/me', { ...body }, opts),
  clockOut: (body: ClockBody, opts?: CrewActionOptions) =>
    crewPost<{ id: string }>('/execution/attendance/clock-out/me', { ...body }, opts),
  /** Internal attendance log (dispatch / ops). Dates are Manila YYYY-MM-DD, inclusive. */
  log: (params: { from: string; to: string; personId?: string }) => {
    const q = new URLSearchParams({ from: params.from, to: params.to });
    if (params.personId) q.set('personId', params.personId);
    return api.get<AttendanceLog>(`/execution/attendance/log?${q.toString()}`);
  },
};

export interface Vehicle {
  id: string;
  plateNumber: string;
  plate?: string;
  vehicleTypeId: string;
  vehicleTypeCode?: string | null;
  vehicleTypeName?: string | null;
  type?: string;
  ownership: 'owned' | 'subcon';
  partnerId?: string | null;
  partnerName?: string | null;
  status: string;
  standingDriverId?: string | null;
  standingHelperIds?: string[];
  driverName?: string | null;
  currentOdometer?: number | null;
  tankCapacityLitres?: string | null;
  acquisitionCost?: string | null;
  acquisitionDate?: string | null;
  operationalAllocationRate?: string | null;
  costShape?: string | null;
  crewSource?: string | null;
  monthlyCost?: string | null;
  dailyHireCost?: string | null;
  breakEvenTripCount?: number | null;
}

export type VehicleWrite = {
  plateNumber: string;
  vehicleTypeId: string;
  ownership: 'owned' | 'subcon';
  partnerId?: string | null;
  status?: string;
  standingDriverId?: string | null;
  standingHelperIds?: string[];
  currentOdometer?: number;
  tankCapacityLitres?: string | null;
  acquisitionCost?: string | null;
  acquisitionDate?: string | null;
  operationalAllocationRate?: string | null;
  costShape?: string | null;
  crewSource?: string | null;
  monthlyCost?: string | null;
  dailyHireCost?: string | null;
  breakEvenTripCount?: number | null;
};

export const vehiclesApi = {
  list: () => api.get<Vehicle[]>('/vehicles'),
  get: (id: string) => api.get<Vehicle>(`/vehicles/${id}`),
  create: (data: VehicleWrite) => api.post<Vehicle>('/vehicles', data),
  update: (id: string, data: Partial<VehicleWrite>) => api.patch<Vehicle>(`/vehicles/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/vehicles/${id}`),
};

export interface Partner {
  id: string;
  name: string;
  tin?: string | null;
  contact?: string | null;
  contractStart?: string | null;
  contractEnd?: string | null;
  status?: string;
  accuracyScore?: string | null;
}

export type PartnerWrite = {
  name: string;
  tin?: string;
  contact?: string;
  contractStart?: string;
  contractEnd?: string;
  status?: string;
};

export const partnersApi = {
  list: () => api.get<Partner[]>('/partners'),
  create: (data: PartnerWrite) => api.post<Partner>('/partners', data),
  update: (id: string, data: Partial<PartnerWrite>) => api.patch<Partner>(`/fleet/partners/${id}`, data),
};

export interface SubconInvoice {
  id: string;
  partnerId: string;
  invoiceNumber?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  expectedTotal?: string | null;
  actualTotal?: string | null;
  variance?: string | null;
  varianceReason?: string | null;
  status: 'draft_match' | 'draft' | 'variance_review' | 'matched' | 'disputed';
  firstTimeMatch: boolean;
  decision?: string | null;
  decisionNotes?: string | null;
  decidedAt?: string | null;
  createdAt: string;
}

export interface PartnerAccuracy {
  partnerId: string;
  partnerName: string;
  total: number;
  firstTime: number;
  matched: number;
  disputed: number;
  pending: number;
  firstTimeMatchPct: number | null;
}

export const subconInvoicesApi = {
  list: (partnerId?: string) =>
    api.get<SubconInvoice[]>(`/fleet/subcon-invoices${partnerId ? `?partnerId=${partnerId}` : ''}`),
  draftMatch: (data: {
    partnerId: string;
    invoiceNumber?: string;
    periodStart: string;
    periodEnd: string;
    actualTotal: string;
    expectedTotal: string;
  }) => api.post<SubconInvoice>('/fleet/subcon-invoices/match', data),
  decide: (id: string, decision: 'approve' | 'dispute', notes?: string) =>
    api.post<SubconInvoice>(`/fleet/subcon-invoices/${id}/decide`, { decision, notes }),
  partnerAccuracy: () => api.get<PartnerAccuracy[]>('/fleet/subcon-invoices/partner-accuracy'),
};

export interface Client {
  id: string;
  legalName: string;
  tradeName?: string | null;
  tin?: string | null;
  billingEmail?: string | null;
  billingAddress?: string | null;
  tradingAddress?: string | null;
  paymentTerms?: string | null;
  billingPeriod?: 'weekly' | 'fortnightly' | 'monthly';
  rateCardId?: string | null;
  creditLimit?: string | null;
  scanningRequired?: boolean;
  allowedZones?: string[];
  status: string;
  houseAccount?: boolean;
  portalEnabled?: boolean;
  accountManagerId?: string | null;
  contactPerson?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  createdAt?: string;
}

export type ClientWrite = {
  legalName: string;
  tradeName?: string;
  tin?: string;
  billingEmail?: string;
  billingAddress?: string;
  tradingAddress?: string;
  paymentTerms?: string;
  billingPeriod?: 'weekly' | 'fortnightly' | 'monthly';
  rateCardId?: string | null;
  creditLimit?: string;
  scanningRequired?: boolean;
  allowedZones?: string[];
  houseAccount?: boolean;
  portalEnabled?: boolean;
  accountManagerId?: string | null;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
};

export interface ClientPortalUser {
  id: string;
  clientId: string;
  userId: string;
  clientRole: string;
  email: string;
  status: string;
  lastLoginAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const clientsApi = {
  list: (params?: { status?: string }) =>
    api.get<Client[]>(`/clients${params?.status ? `?status=${params.status}` : ''}`),
  get: (id: string) => api.get<Client & { addresses: Address[] }>(`/clients/${id}`),
  create: (data: ClientWrite) => api.post<Client>('/clients', data),
  update: (id: string, data: Partial<ClientWrite> & { status?: string }) =>
    api.patch<Client>(`/clients/${id}`, data),
  activate: (id: string) => api.post<Client>(`/clients/${id}/activate`),
  deactivate: (id: string) => api.post<Client>(`/clients/${id}/deactivate`),
  listUsers: (id: string) => api.get<ClientPortalUser[]>(`/clients/${id}/users`),
  updateUser: (
    id: string,
    userId: string,
    data: { clientRole?: string; status?: 'active' | 'disabled' | 'pending_invite' },
  ) => api.patch<ClientPortalUser>(`/clients/${id}/users/${userId}`, data),
  removeUser: (id: string, userId: string) =>
    api.delete<{ removed: true; id: string }>(`/clients/${id}/users/${userId}`),
  inviteUser: (
    id: string,
    data: { email: string; password: string; clientRole: string; suppressDelivery?: boolean },
  ) =>
    api.post<{
      id: string;
      email?: string;
      clientRole?: string;
      emailDelivery?: { mode: string; message?: string };
    }>(`/clients/${id}/users/invite`, data),
};

export interface AuditLogRow {
  id: string;
  actorId?: string | null;
  actorRole?: string | null;
  actorEmail?: string | null;
  action: string;
  entityType: string;
  entityId: string;
  before?: unknown;
  after?: unknown;
  reason?: string | null;
  correlationId?: string | null;
  metadata?: Record<string, unknown> | null;
  source?: string | null;
  createdAt: string;
}

export const auditApi = {
  list: (params?: {
    actorId?: string;
    action?: string;
    entityType?: string;
    entityId?: string;
    correlationId?: string;
    q?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== '') qs.set(k, String(v));
      }
    }
    const q = qs.toString();
    return api.get<{ rows: AuditLogRow[]; total: number; limit: number; offset: number }>(
      `/audit${q ? `?${q}` : ''}`,
    );
  },
};

export interface Address {
  id: string;
  clientId?: string | null;
  isInternalHub?: boolean;
  label: string;
  fullAddress: string;
  latitude?: number | null;
  longitude?: number | null;
  zoneId?: string | null;
  contactPerson?: string | null;
  contactNumber?: string | null;
  geocodedAt?: string | null;
  createdAt?: string;
}

export type AddressWrite = {
  clientId?: string | null;
  label: string;
  fullAddress: string;
  latitude?: number;
  longitude?: number;
  contactPerson?: string;
  contactNumber?: string;
  isInternalHub?: boolean;
};

export interface AddressImportPreviewRow {
  label: string;
  fullAddress: string;
  contactPerson?: string;
  contactNumber?: string;
  latitude?: number;
  longitude?: number;
  zoneId?: string | null;
  zoneCode?: string | null;
  coordinatePrecision?: 'csv' | 'geocode' | null;
  change: 'new' | 'update';
}

export interface AddressImportPreview {
  rows: AddressImportPreviewRow[];
  errors: Array<{ row: number; field?: string; message: string }>;
}

export const addressesApi = {
  list: (clientId?: string) => api.get<Address[]>(`/addresses${clientId ? `?clientId=${clientId}` : ''}`),
  create: (data: AddressWrite) => api.post<Address>('/addresses', data),
  update: (id: string, data: Partial<Omit<AddressWrite, 'contactPerson' | 'contactNumber'>> & {
    contactPerson?: string | null;
    contactNumber?: string | null;
  }) => api.patch<Address>(`/addresses/${id}`, data),
  /** Unused entries are deleted; entries used by trips are archived (trips keep them). */
  remove: (id: string) =>
    api.delete<{ id: string; deleted: boolean; archived: boolean }>(`/addresses/${id}`),
  importPreview: (clientId: string, csv: string) =>
    api.post<AddressImportPreview>(`/clients/${clientId}/addresses/import/preview`, { csv }),
  import: (clientId: string, csv: string) =>
    api.post<{ imported: number; preview: AddressImportPreview }>(
      `/clients/${clientId}/addresses/import`,
      { csv },
    ),
};

export interface PersonSystemRole {
  id: string;
  name: string;
}

export interface Person {
  id: string;
  name?: string;
  fullName?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  role?: string;
  engagementType?: 'employee' | 'contractor';
  licenceNumber?: string;
  licenceExpiry?: string | null;
  status: string;
  isCrew?: boolean;
  isSales?: boolean;
  salesEngagement?: 'full_time' | 'part_time' | 'commission_only' | null;
  hasLogin?: boolean;
  loginEmail?: string | null;
  loginStatus?: string | null;
  userAccountId?: string | null;
  systemRoles?: PersonSystemRole[];
  supplyingPartnerId?: string | null;
  partnerName?: string | null;
  /** Client organisation of this person's client-portal login, if any. */
  clientId?: string | null;
}

export type PersonWrite = {
  fullName: string;
  mobile?: string;
  engagementType?: 'employee' | 'contractor';
  isCrew?: boolean;
  isSales?: boolean;
  salesEngagement?: 'full_time' | 'part_time' | 'commission_only' | null;
  licenceNumber?: string;
  licenceExpiry?: string;
  supplyingPartnerId?: string | null;
};

export type LinkAccountPayload = {
  email: string;
  password: string;
  roleIds?: string[];
  clientId?: string | null;
};

export type UpdateAccountPayload = {
  email?: string;
  password?: string;
  roleIds?: string[];
  status?: 'active' | 'disabled' | 'pending_invite';
  clientId?: string | null;
};

export const peopleApi = {
  list: (params?: {
    isCrew?: boolean;
    includeArchived?: boolean;
    supplyingPartnerId?: string;
    engagementType?: 'employee' | 'contractor';
  }) => {
    const qs = new URLSearchParams();
    if (params?.isCrew !== undefined) qs.set('isCrew', String(params.isCrew));
    if (params?.includeArchived) qs.set('includeArchived', 'true');
    if (params?.supplyingPartnerId) qs.set('supplyingPartnerId', params.supplyingPartnerId);
    if (params?.engagementType) qs.set('engagementType', params.engagementType);
    const q = qs.toString() ? `?${qs}` : '';
    return api.get<Person[]>(`/people${q}`);
  },
  get: (id: string) => api.get<Person>(`/people/${id}`),
  create: (data: PersonWrite) => api.post<Person>('/people', data),
  update: (id: string, data: Partial<PersonWrite> & { status?: string }) =>
    api.patch<Person>(`/people/${id}`, data),
  linkAccount: (id: string, data: LinkAccountPayload) =>
    api.post<Person>(`/people/${id}/link-account`, data),
  updateAccount: (id: string, data: UpdateAccountPayload) =>
    api.patch<Person>(`/people/${id}/account`, data),
  setRoles: (id: string, roleIds: string[]) =>
    api.put<Person>(`/people/${id}/roles`, { roleIds }),
  archive: (id: string) => api.post<Person>(`/people/${id}/archive`),
  restore: (id: string) => api.post<Person>(`/people/${id}/restore`),
  remove: (id: string) => api.delete<{ deleted: boolean; id: string }>(`/people/${id}`),
  /** Phones this person is signed in on (crew Android app). */
  devices: (id: string) => api.get<DeviceSession[]>(`/people/${id}/devices`),
  signOutDevice: (id: string, sessionId: string) =>
    api.post<{ ok: true }>(`/people/${id}/devices/${sessionId}/revoke`, {}),
};

export interface DeviceSession {
  id: string;
  platform: string;
  deviceName: string | null;
  appVersion: string | null;
  appVersionCode: number | null;
  signedInAt: string;
  lastSeenAt: string;
  expiresAt: string;
  pushEnabled: boolean;
}

export interface Zone {
  id: string;
  code: string;
  name: string;
  coverage?: string;
  coverageDescription?: string | null;
  hasPolygon?: boolean;
  status?: string;
}

export interface ZonePair {
  id: string;
  originZoneId: string;
  destinationZoneId: string;
  oneWayDistanceKm?: string | null;
  typicalDriveTimeMinutes?: number | null;
  dispatchLeadTimeHours?: string | null;
}

export const zonesApi = {
  list: () => api.get<Zone[]>('/zones'),
  create: (data: { code: string; name: string; coverage?: string; coverageDescription?: string }) =>
    api.post<Zone>('/zones', data),
  update: (id: string, data: Partial<{ name: string; coverageDescription: string; status: string }>) =>
    api.patch<Zone>(`/zones/${id}`, data),
  seed: () => api.post<Zone[]>('/zones/seed'),
  getPolygon: (id: string) =>
    api.get<{ geojson: { type: 'MultiPolygon'; coordinates: number[][][][] } | null }>(
      `/zones/${id}/polygon`,
    ),
  savePolygon: (
    id: string,
    geojson: { type: 'MultiPolygon' | 'Polygon'; coordinates: number[][][][] | number[][][] },
  ) => api.put<Zone>(`/zones/${id}/polygon`, geojson),
  clearPolygon: (id: string) => api.delete<Zone>(`/zones/${id}/polygon`),
  previewGeoJsonImport: (geojson: unknown) =>
    api.post<{
      featureCount: number;
      matched: number;
      unmatched: number;
      invalid: number;
      rows: Array<{
        index: number;
        featureLabel: string;
        matchedZoneId: string | null;
        matchedZoneCode: string | null;
        matchedZoneName: string | null;
        matchMethod: 'code' | 'name' | 'none';
        geometryType: string | null;
        status: 'matched' | 'unmatched' | 'invalid';
        error?: string;
      }>;
    }>('/zones/import-geojson/preview', { geojson }),
  applyGeoJsonImport: (geojson: unknown, onlyIndexes?: number[]) =>
    api.post<{
      applied: number;
      skipped: number;
      results: Array<{ index: number; zoneCode: string | null; status: string; error?: string }>;
    }>('/zones/import-geojson', { geojson, onlyIndexes }),
};

export const zonePairsApi = {
  list: () => api.get<ZonePair[]>('/zone-pairs'),
  upsert: (data: {
    originZoneId: string;
    destinationZoneId: string;
    oneWayDistanceKm?: string;
    typicalDriveTimeMinutes?: number;
    dispatchLeadTimeHours?: string;
  }) => api.post<ZonePair>('/zone-pairs', data),
};

export interface VehicleType {
  id: string;
  code: string;
  name: string;
  payloadKg: number;
  tollClass: number;
  kmPerLitre: string;
  status: string;
}

export const vehicleTypesApi = {
  list: () => api.get<VehicleType[]>('/vehicle-types'),
  create: (data: {
    code: string;
    name: string;
    payloadKg: number;
    tollClass: number;
    kmPerLitre: string;
  }) => api.post<VehicleType>('/vehicle-types', data),
  update: (
    id: string,
    data: Partial<{
      code: string;
      name: string;
      payloadKg: number;
      tollClass: number;
      kmPerLitre: string;
      status: string;
    }>,
  ) => api.patch<VehicleType>(`/vehicle-types/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/vehicle-types/${id}`),
  seed: () => api.post<VehicleType[]>('/vehicle-types/seed'),
};

export interface RateCard {
  id: string;
  name: string;
  status: string;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  notes?: string | null;
  version?: number;
  currency?: string;
}

export interface RateCell {
  id: string;
  rateCardId: string;
  originZoneId: string;
  destinationZoneId: string;
  vehicleTypeId: string;
  amount: string;
  isMinimumFloor: boolean;
}

export interface AccessorialRule {
  id: string;
  rateCardId: string;
  chargeType: string;
  description: string;
  rate: string;
  unit?: string;
  triggerCondition?: string | null;
  freeAllowance?: string | null;
  increment?: string | null;
  capAmount?: string | null;
  capBasis?: string | null;
  requiresApproval?: boolean;
  billableByDefault?: boolean;
}

export const rateCardsApi = {
  list: () => api.get<RateCard[]>('/rate-cards'),
  get: (id: string) => api.get<RateCard>(`/rate-cards/${id}`),
  create: (data: { name: string; notes?: string; effectiveFrom?: string }) =>
    api.post<RateCard>('/rate-cards', data),
  update: (id: string, data: Partial<{ name: string; notes: string; effectiveFrom: string | null }>) =>
    api.patch<RateCard>(`/rate-cards/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/rate-cards/${id}`),
  listCells: (id: string) => api.get<RateCell[]>(`/rate-cards/${id}/cells`),
  upsertCells: (
    id: string,
    cells: Array<{
      originZoneId: string;
      destinationZoneId: string;
      vehicleTypeId: string;
      amount: string;
      isMinimumFloor?: boolean;
    }>,
  ) => api.put<RateCell[]>(`/rate-cards/${id}/cells`, { cells }),
  deleteCell: (id: string, cellId: string) =>
    api.delete<{ ok: boolean }>(`/rate-cards/${id}/cells/${cellId}`),
  listAccessorials: (id: string) => api.get<AccessorialRule[]>(`/rate-cards/${id}/accessorials`),
  upsertAccessorial: (
    id: string,
    data: {
      id?: string;
      chargeType: string;
      description: string;
      rate: string;
      unit?: string;
      triggerCondition?: string;
      freeAllowance?: string;
      increment?: string;
      capAmount?: string;
      requiresApproval?: boolean;
      billableByDefault?: boolean;
    },
  ) => api.post<AccessorialRule>(`/rate-cards/${id}/accessorials`, data),
  deleteAccessorial: (id: string, ruleId: string) =>
    api.delete<{ ok: boolean }>(`/rate-cards/${id}/accessorials/${ruleId}`),
  activate: (id: string) =>
    api.post<{ rateCard: RateCard; warnings: string[]; marginFloor: number | null; payImpact: PayImpactPreview }>(
      `/rate-cards/${id}/activate`,
    ),
  payImpact: (id: string) => api.get<PayImpactPreview>(`/rate-cards/${id}/pay-impact`),
  previewImport: (id: string, csv: string) =>
    api.post<RateCardImportPreview>(`/rate-cards/${id}/import/preview`, { csv }),
  import: (id: string, csv: string) =>
    api.post<{ imported: number; preview: RateCardImportPreview }>(`/rate-cards/${id}/import`, { csv }),
};

export interface PayImpactPreview {
  threshold: string;
  marginFloorPct?: number | null;
  totalCells: number;
  belowThresholdCount: number;
  uniqueLaneCount?: number;
  crkUniqueLaneCount?: number;
  thresholdSource?: string;
  sixWVehicleTypeCodes?: string[];
  nearThresholdViolations?: Array<{
    originZoneCode: string;
    destinationZoneCode: string;
    expectedAmount: number;
    actualAmount: number;
    reason: string;
  }>;
  samples: Array<{ originZoneId: string; destinationZoneId: string; amount: string }>;
}

export interface RateCardImportDiffRow {
  origin: string;
  destination: string;
  vehicle_type: string;
  current: string | null;
  proposed: string;
  change: 'new' | 'update' | 'unchanged' | 'error';
  error?: string;
}

export interface RateCardImportPreview {
  totalRows: number;
  newCount: number;
  updateCount: number;
  errorCount?: number;
  errors?: Array<{ row: number; field?: string; message: string }>;
  diff: RateCardImportDiffRow[];
}

export interface PayCard {
  id: string;
  name: string;
  status: string;
  effectiveFrom?: string | null;
  pairedRateCardId?: string | null;
  version?: number;
  classificationConfig?: Record<string, {
    threshold: string;
    basis: 'own' | '6w';
    driverMnl: string;
    driverCrk: string;
    helperMnl: string;
    helperCrk: string;
  }>;
}

export interface PayCell {
  id: string;
  payCardId: string;
  originZoneId: string;
  destinationZoneId: string;
  vehicleTypeId: string;
  driverAmount: string;
  helperAmount: string;
  extraStopAmount: string;
}

export const payCardsApi = {
  list: () => api.get<PayCard[]>('/pay-cards'),
  get: (id: string) => api.get<PayCard>(`/pay-cards/${id}`),
  create: (data: { name: string }) => api.post<PayCard>('/pay-cards', data),
  update: (
    id: string,
    data: Partial<{
      name: string;
      pairedRateCardId: string | null;
      classificationConfig: PayCard['classificationConfig'];
    }>,
  ) => api.patch<PayCard>(`/pay-cards/${id}`, data),
  remove: (id: string) => api.delete<{ ok: boolean }>(`/pay-cards/${id}`),
  activate: (id: string) => api.post<{ payCard: PayCard }>(`/pay-cards/${id}/activate`),
  listCells: (id: string) => api.get<PayCell[]>(`/pay-cards/${id}/cells`),
  upsertCells: (
    id: string,
    cells: Array<{
      originZoneId: string;
      destinationZoneId: string;
      vehicleTypeId: string;
      driverAmount: string;
      helperAmount: string;
      extraStopAmount?: string;
    }>,
  ) => api.put<PayCell[]>(`/pay-cards/${id}/cells`, { cells }),
  deleteCell: (id: string, cellId: string) =>
    api.delete<{ ok: boolean }>(`/pay-cards/${id}/cells/${cellId}`),
  previewImport: (id: string, csv: string) =>
    api.post<PayCardImportPreview>(`/pay-cards/${id}/import/preview`, { csv }),
  import: (id: string, csv: string) =>
    api.post<{ imported: number; preview: PayCardImportPreview }>(`/pay-cards/${id}/import`, { csv }),
};

export interface PayCardImportDiffRow {
  originZoneCode: string;
  destinationZoneCode: string;
  vehicleTypeCode: string;
  currentDriverAmount: string | null;
  currentHelperAmount: string | null;
  currentExtraStopAmount: string | null;
  proposedDriverAmount: string;
  proposedHelperAmount: string;
  proposedExtraStopAmount: string;
  change: 'new' | 'update' | 'unchanged' | 'error';
  error?: string;
}

export interface PayCardImportPreview {
  totalRows: number;
  newCount: number;
  updateCount: number;
  errorCount?: number;
  errors?: Array<{ row: number; field?: string; message: string }>;
  diff: PayCardImportDiffRow[];
}

export interface BillingRun {
  id: string;
  clientId?: string;
  period: string;
  periodStart?: string;
  periodEnd?: string;
  periodLabel?: string | null;
  status: string;
  totalAmount?: string;
  subtotal?: string;
  vatAmount?: string;
  total?: string;
  tripCount?: number;
  xeroInvoiceId?: string | null;
  xeroSyncState?: string | null;
  xeroSyncError?: string | null;
  paymentStatus?: string | null;
  invoiceNumber?: string | null;
  finalizedAt?: string | null;
  finalizedBy?: string | null;
  voidedAt?: string | null;
  voidReason?: string | null;
  supersededByRunId?: string | null;
  externalReference?: string | null;
  createdAt: string;
  /** Server-computed — prefer over client-side gates when present. */
  canFinalize?: boolean;
  canDiscard?: boolean;
}

export interface FrozenInvoiceLine {
  tripId: string;
  tripNumber: string | null;
  amount: string;
  lines: Array<{
    id: string;
    chargeType: string;
    description: string;
    quantity: string;
    unitRate: string;
    amount: string;
  }>;
}

export interface CreditNote {
  id: string;
  invoiceRunId: string;
  creditNoteNumber: string;
  amount: string;
  vatAmount: string;
  total: string;
  reason: string;
  status: string;
  issuedAt: string;
  createdAt: string;
}

export interface InvoiceRunDetail {
  run: BillingRun;
  frozenLines: FrozenInvoiceLine[] | null;
  trips: Trip[];
  creditNotes: CreditNote[];
}

export interface ReadyToBillGates {
  gates: Record<string, boolean>;
  allPass: boolean;
}

export interface TripInvoiceAmountPreview {
  tripId: string;
  tripNumber: string;
  status: string;
  finalAmount?: string | null;
  invoiceAmount: string;
  vatAmount: string;
  totalWithVat: string;
  exclusiveFailedTrip: boolean;
  rawBillableSum: string;
  billableLines: Array<{
    id: string;
    chargeType: string;
    description?: string | null;
    amount: string;
    billable: boolean;
    approvalState?: string | null;
  }>;
}

export const billingApi = {
  /** Client invoice PDF — finalised runs only. */
  downloadInvoicePdf: (id: string, invoiceNumber?: string | null) =>
    downloadFile(`/billing/invoice-runs/${id}/pdf`, `${invoiceNumber ?? 'invoice'}.pdf`),
  listRuns: () => api.get<BillingRun[]>('/billing/runs'),
  createRun: (data: {
    clientId: string;
    period?: string;
    periodStart?: string;
    periodEnd?: string;
  }) => api.post<BillingRun>('/billing/runs', data),
  discardRun: (id: string) => api.delete<{ discarded: true; id: string }>(`/billing/runs/${id}`),
  getRun: (id: string) => api.get<InvoiceRunDetail>(`/billing/runs/${id}`),
  finalizeRun: (id: string, data?: { idempotencyKey?: string; externalReference?: string }) =>
    api.post<{ run: BillingRun; idempotentReplay: boolean }>(`/billing/runs/${id}/finalize`, data ?? {}),
  createCreditNote: (
    id: string,
    data: { amount: string; reason: string; lines?: unknown[]; idempotencyKey?: string },
  ) => api.post<CreditNote>(`/billing/runs/${id}/credit-notes`, data),
  markCollected: (id: string, paidAt?: string) =>
    api.post<{ run: BillingRun; alreadyPaid: boolean }>(`/billing/runs/${id}/mark-collected`, paidAt ? { paidAt } : {}),
  reviewQueue: () => api.get<Trip[]>('/billing/review-queue'),
  getGates: (tripId: string) => api.get<ReadyToBillGates>(`/billing/trips/${tripId}/gates`),
  previewInvoiceAmount: (tripId: string) =>
    api.get<TripInvoiceAmountPreview>(`/billing/trips/${tripId}/invoice-amount`),
  markReady: (tripId: string) => api.post<Trip>(`/billing/trips/${tripId}/ready`),
  approveChargeLine: (chargeLineId: string) =>
    api.post(`/billing/charge-lines/${chargeLineId}/approve`),
  waiveChargeLine: (chargeLineId: string, reason: string) =>
    api.post(`/billing/charge-lines/${chargeLineId}/waive`, { reason }),
  pushToXero: (id: string) =>
    api.post<{ run: BillingRun; xero: XeroPushResult }>(`/billing/invoice-runs/${id}/push`),
};

export interface XeroPushResult {
  success: boolean;
  mode: string;
  invoiceId: string | null;
  message: string;
}

export interface Payout {
  id: string;
  period: string;
  periodLabel?: string | null;
  periodStart?: string;
  periodEnd?: string;
  status: string;
  total?: string;
  totalAmount?: string;
  finalizedAt?: string | null;
  paidAt?: string | null;
  canFinalize?: boolean;
  createdAt: string;
}

export interface PayoutEntry {
  id: string;
  tripId: string;
  personId: string;
  personName?: string;
  role: string;
  baseAmount: string;
  extraStopAmount?: string | null;
  overrideAmount?: string | null;
  overrideReason?: string | null;
  state: string;
  payoutRunId?: string | null;
  /** 'standard' | 'correction' (replacement amount) | 'adjustment' (difference carried from a finalised run) */
  entryKind?: string;
  rootEntryId?: string | null;
  /** When the pay was earned (run detail only). */
  earnedAt?: string;
  /** Earned before the run's period and rolled forward into it (run detail only). */
  fromEarlierPeriod?: boolean;
  createdAt: string;
}

/** One pay correction: what the entry was worth before (`from`) and after (`to`). */
export interface PayCorrectionHistoryItem {
  id: string;
  rootEntryId: string;
  originalEntryId: string;
  correctionEntryId: string;
  personId: string;
  personName: string | null;
  tripId: string;
  role: string;
  from: string | null;
  to: string | null;
  /** Amount actually queued: the new amount, or only the difference when carried forward. */
  entryAmount: string | null;
  mode: 'replace' | 'carry_forward' | string;
  reason: string;
  byUserId: string | null;
  by: string | null;
  at: string;
}

export interface PayCorrectionResult {
  correction: PayoutEntry;
  mode: 'replace' | 'carry_forward';
  previousAmount: string;
  newAmount: string;
  entryAmount: string;
  message: string;
}

export interface PayoutStatement {
  id: string;
  payoutRunId: string;
  personId: string;
  personName?: string;
  grossTotal: string;
  deductionsTotal: string;
  netTotal: string;
  entryIds: string[];
  lines: Array<{
    entryId: string;
    tripId: string;
    role: string;
    amount: string;
    state: string;
    kind?: string;
    earnedAt?: string;
    fromEarlierPeriod?: boolean;
  }>;
  status: string;
  finalizedAt?: string | null;
  createdAt: string;
  corrections?: PayCorrectionHistoryItem[];
}

export interface MyPayoutStatement extends PayoutStatement {
  periodLabel: string | null;
  periodStart: string;
  periodEnd: string;
  runStatus: string;
  runPaidAt?: string | null;
}

export interface PayoutRunDetail {
  run: Payout & { periodStart: string; periodEnd: string; finalizedAt?: string | null; paidAt?: string | null };
  entries: PayoutEntry[];
  statements: PayoutStatement[];
  corrections?: PayCorrectionHistoryItem[];
}

export const payoutsApi = {
  list: () => api.get<Payout[]>('/payouts'),
  create: (data: { period: string }) => api.post<Payout>('/payouts', data),
  getDetail: (id: string) => api.get<PayoutRunDetail>(`/pay/payout-runs/${id}`),
  listEntries: (id: string) => api.get<PayoutEntry[]>(`/pay/payout-runs/${id}/entries`),
  finalize: (id: string) =>
    api.post<{ run: Payout; statements: PayoutStatement[]; idempotent?: boolean }>(
      `/pay/payout-runs/${id}/finalize`,
    ),
  markPaid: (id: string) => api.post<Payout>(`/pay/payout-runs/${id}/mark-paid`),
  getStatement: (id: string, personId: string) =>
    api.get<PayoutStatement>(`/pay/payout-runs/${id}/statements/${personId}`),
  /** `amount` is the NEW full amount — it replaces the entry's current amount. */
  createCorrection: (data: { originalEntryId: string; amount: string; reason: string }) =>
    api.post<PayCorrectionResult>('/pay/entries/corrections', data),
  entryCorrections: (entryId: string) =>
    api.get<PayCorrectionHistoryItem[]>(`/pay/entries/${entryId}/corrections`),
  myStatements: () => api.get<MyPayoutStatement[]>('/earnings/statements'),
};

export interface CostEntry {
  id: string;
  tripId: string;
  /** Server rows carry `category` (the crew form sends `type`, stored as category). */
  category: string;
  /** @deprecated never returned by the server; use `category`. */
  type?: string;
  amount: string;
  description?: string;
  receiptKey?: string | null;
  quantity?: string | null;
  odometerAtEntry?: number | null;
  vendor?: string | null;
  createdAt: string;
}

export interface CostEntryWrite {
  type: string;
  amount: string;
  description?: string;
  receiptKey: string;
  litres?: string;
  odometer?: number;
  vendor?: string;
}

export interface TripCostAllocation {
  id: string;
  costEntryId: string;
  tripId: string;
  allocatedAmount: string;
  basis: 'distance' | 'equal_per_trip' | 'distance_fallback_equal';
  distanceKm?: string | null;
  runReference?: string | null;
  validatedAt?: string | null;
  validatedBy?: string | null;
  createdAt: string;
  updatedAt: string;
  entryCategory: string;
  entryDescription?: string | null;
  entryClass: string;
  entryAmount: string;
}

export interface TripCostDetail {
  tripId: string;
  direct: CostEntry[];
  allocated: TripCostAllocation[];
  totalDirect: string;
  totalAllocated: string;
  total: string;
}

export const costsApi = {
  list: (tripId: string) => api.get<TripCostDetail>(`/trips/${tripId}/costs`),
  create: (tripId: string, data: CostEntryWrite, opts?: CrewActionOptions) =>
    crewPost<CostEntry>(`/trips/${tripId}/costs`, { ...data }, opts),
};

export interface FleetCostEntry {
  id: string;
  class: 'trip_direct' | 'vehicle_period' | 'overhead';
  category: string;
  vehicleId?: string | null;
  tripId?: string | null;
  amount: string;
  description?: string | null;
  /** Storage key (or, on older rows, an inline image) of the receipt photo. */
  receiptKey?: string | null;
  loggedAt: string;
  allocatedTotal: string;
  isAllocated: boolean;
}

export interface CostAllocationRow {
  id: string;
  costEntryId: string;
  tripId: string;
  allocatedAmount: string;
  basis: 'distance' | 'equal_per_trip' | 'distance_fallback_equal';
  distanceKm?: string | null;
  runReference?: string | null;
  validatedAt?: string | null;
}

export type CostRollupGroupBy = 'trip' | 'lane' | 'zone' | 'client' | 'vehicle' | 'km';

export interface CostRollupResponse {
  groupBy: CostRollupGroupBy;
  rows: Array<Record<string, unknown>>;
}

export interface OwnedVsSubconBucket {
  totalCost: string;
  totalKm: number;
  tripCount: number;
  avgCostPerKm: number | null;
  avgCostPerTrip: number | null;
}

export interface OwnedVsSubconResult {
  periodStart: string;
  periodEnd: string;
  owned: OwnedVsSubconBucket;
  subcon: OwnedVsSubconBucket;
  cheaperPerKm: 'owned' | 'subcon' | null;
}

export const fleetCostsApi = {
  list: () => api.get<FleetCostEntry[]>('/fleet/costs'),
  create: (data: {
    class: 'trip_direct' | 'vehicle_period' | 'overhead';
    category: string;
    vehicleId?: string;
    tripId?: string;
    amount: string;
    quantity?: string;
    description?: string;
  }) => api.post<FleetCostEntry>('/fleet/costs', data),
  allocations: (costId: string) =>
    api.get<{
      costEntryId: string;
      currentRunReference: string | null;
      tripIds: string[];
      basis: 'distance' | 'equal_per_trip';
      allocations: CostAllocationRow[];
    }>(`/fleet/costs/${costId}/allocations`),
  allocate: (costId: string, data: { tripIds: string[]; basis: 'distance' | 'equal_per_trip' }) =>
    api.post<CostAllocationRow[]>(`/fleet/costs/${costId}/allocate`, data),
  validateRun: (runReference: string) =>
    api.post<CostAllocationRow[]>(`/fleet/cost-allocation-runs/${encodeURIComponent(runReference)}/validate`),
  rollups: (groupBy: CostRollupGroupBy) =>
    api.get<CostRollupResponse>(`/fleet/cost-rollups?groupBy=${groupBy}`),
  ownedVsSubcon: (periodStart: string, periodEnd: string) =>
    api.get<OwnedVsSubconResult>(
      `/fleet/owned-vs-subcon?periodStart=${encodeURIComponent(periodStart)}&periodEnd=${encodeURIComponent(periodEnd)}`,
    ),
};

export interface EarningsSummary {
  period: string;
  /** Current Manila fortnight, e.g. "2026-10-H2". */
  periodLabel?: string;
  periodStart?: string;
  periodEnd?: string;
  /** Earned in the current fortnight (whole pesos). */
  periodEarnings?: string;
  periodTripsCompleted?: number;
  /** All time (whole pesos). */
  totalEarnings: string;
  tripsCompleted: number;
  /** Earned, not paid yet (waiting approval + approved). */
  pendingPayout?: string;
}

export interface EarningsEntry {
  id: string;
  tripId: string;
  role: string;
  baseAmount: string;
  extraStopAmount?: string | null;
  overrideAmount?: string | null;
  state: string;
  entryKind?: string;
  createdAt: string;
}

export const earningsApi = {
  summary: () => api.get<EarningsSummary>('/earnings/summary'),
  history: () => api.get<EarningsEntry[]>('/earnings/history'),
};

export interface ScanEvent {
  id: string;
  tripStopId: string;
  manifestLineId?: string | null;
  barcode: string;
  result: 'counted' | 'over' | 'not_on_manifest' | 'wrong_stop' | 'pending_confirmation' | 'corrected';
  entryMethod?: string;
  offlineCaptured?: boolean;
  scannedAt: string;
  stopSequence?: number;
  stopType?: string;
  tripId?: string;
  tripNumber?: string;
}

export interface ScanCount {
  barcode: string;
  expected: number;
  scanned: number;
  variance: number;
  status?: 'ok' | 'short' | 'over' | 'not_on_manifest' | 'wrong_stop';
}

export interface ScanResult {
  scan: ScanEvent;
  counts: ScanCount[];
}

export const scanningApi = {
  counts: (stopId: string) => api.get<ScanCount[]>(`/scanning/stops/${stopId}/counts`),
  listOvers: (stopId: string) => api.get<ScanEvent[]>(`/scanning/stops/${stopId}/overs`),
  scan: (data: {
    tripStopId: string;
    barcode: string;
    entryMethod?: string;
    offlineCaptured?: boolean;
    clientEventId?: string;
    scannedAt?: string;
  }) =>
    apiRequest<ScanResult>('/scanning/scan', {
      method: 'POST',
      body: data,
      headers: data.clientEventId ? { 'Idempotency-Key': data.clientEventId } : {},
    }),
  pending: (stopId: string) => api.get<ScanEvent[]>(`/scanning/stops/${stopId}/pending`),
  pendingForTrip: (tripId: string) => api.get<ScanEvent[]>(`/scanning/trips/${tripId}/pending`),
  pendingQueue: () => api.get<ScanEvent[]>('/scanning/pending'),
  resolve: (scanId: string, decision: 'accept' | 'reject', reasonCode?: string) =>
    api.post(`/scanning/scans/${scanId}/resolve`, { decision, reasonCode }),
  correctOver: (scanId: string, data: { reason: string; device?: string }, opts?: CrewActionOptions) =>
    crewPost<ScanResult>(`/scanning/scans/${scanId}/correct-over`, { ...data }, { clientEventId: opts?.clientEventId }),
  acknowledgeOver: (scanId: string, data?: { device?: string }, opts?: CrewActionOptions) =>
    crewPost<ScanResult & { exception: unknown }>(
      `/scanning/scans/${scanId}/acknowledge-over`,
      { ...(data ?? {}) },
      { clientEventId: opts?.clientEventId },
    ),
  previewManifest: (stopId: string, csv: string) =>
    api.post<{
      totalRows: number;
      uniqueBarcodes: number;
      preview: Array<{ barcode: string; qty: number; change: string }>;
    }>(`/scanning/stops/${stopId}/manifest/preview`, { csv }),
  importManifest: (stopId: string, csv: string) =>
    api.post<{ imported: number }>(`/scanning/stops/${stopId}/manifest/import`, { csv }),
};

/** @deprecated use scanningApi.scan — kept only for the legacy /scans alias route. */
export const scansApi = {
  submit: (data: { barcode: string; stopId?: string; tripId?: string; entryMethod?: string; offlineCaptured?: boolean }) =>
    api.post('/scans', data),
};

export interface Prospect {
  id: string;
  companyName: string;
  contactName?: string;
  status: string;
  createdAt: string;
}

export interface SalesDashboardTrip {
  id: string;
  tripNumber: string;
  status: string;
  scheduledPickupAt?: string;
  clientName: string;
  quotedAmount?: string | null;
  finalAmount?: string | null;
  tripRevenue?: string | null;
  allocatedCost?: string | null;
  grossProfit?: string | null;
  commissionAmount?: string | null;
  commissionProvisional?: boolean | null;
}

export interface SalesDashboard {
  agent: {
    id: string;
    personId: string;
    engagementLevel: 'full_time' | 'part_time' | 'commission_only';
    status: string;
  };
  visibility: 'per_trip_cost_gp' | 'aggregate_gp';
  clientCount: number;
  clients: Array<{ id: string; legalName: string; status: string }>;
  commissionProvisional: string;
  commissionEarned: string;
  grossProfitAggregate: string;
  grossProfitProvisional: string;
  grossProfitEarned: string;
  recentTrips: SalesDashboardTrip[];
}

export const prospectsApi = {
  list: () => api.get<Prospect[]>('/sales/prospects'),
  create: (data: Partial<Prospect> & { notes?: string; contactEmail?: string; contactPhone?: string }) =>
    api.post<Prospect>('/sales/prospects', data),
};

export const salesApi = {
  dashboard: () => api.get<SalesDashboard>('/sales/dashboard'),
  prospects: () => api.get<Prospect[]>('/sales/prospects'),
};

export interface CommissionBand {
  id?: string;
  fromAmount: string;
  toAmount?: string | null;
  rate: string;
  sortOrder: number;
}

export interface BonusRule {
  id?: string;
  metric: 'trip_count' | 'revenue' | 'gross_profit';
  threshold: string;
  amount: string;
  windowDays?: number | null;
  oneOff: boolean;
}

export interface CommissionPlan {
  id: string;
  name: string;
  base: string;
  structure: string;
  flatRate?: string | null;
  floorRule: boolean;
  bonusEligible: boolean;
  payOnCollection: boolean;
  version: number;
  status: 'draft' | 'active' | 'archived';
  parentPlanId?: string | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  activatedAt?: string | null;
  activatedBy?: string | null;
  activatedByName?: string | null;
  createdBy?: string | null;
  createdByName?: string | null;
  createdAt: string;
}

export interface CommissionPlanDetail {
  plan: CommissionPlan;
  bands: CommissionBand[];
  bonusRules: BonusRule[];
}

export interface CommissionSettlementRun {
  id: string;
  agentId: string;
  periodStart: string;
  periodEnd: string;
  /** Inclusive Manila display range, e.g. 2026-08-01 → 2026-08-31 */
  period?: string;
  revenueTotal: string;
  allocatedCostTotal: string;
  commissionableGpTotal: string;
  commissionTotal: string;
  bonusTotal: string;
  netPayable: string;
  status: 'draft' | 'finalized';
  finalizedAt?: string | null;
  canDiscard?: boolean;
  canFinalize?: boolean;
  createdAt: string;
}

export interface CommissionSettlementRunDetail {
  run: CommissionSettlementRun;
  entries: Array<{
    id: string;
    tripId: string;
    tripRevenue: string;
    allocatedCost: string;
    grossProfit: string;
    applicableRate?: string | null;
    amount: string;
    state: string;
    tripNumber: string | null;
    clientName: string | null;
  }>;
  bonusAwards: Array<{ id: string; bonusRuleId: string; amount: string; ruleLabel: string }>;
}

export interface SalesAgentSummary {
  id: string;
  personId: string;
  personName: string;
  engagementLevel: 'full_time' | 'part_time' | 'commission_only';
  commissionPlanId?: string | null;
  planAssignedAt?: string | null;
  planAssignedBy?: string | null;
  planAssignedByName?: string | null;
  status: string;
}

export const commissionApi = {
  listAgents: () => api.get<SalesAgentSummary[]>('/commission/agents'),
  assignAgentPlan: (data: { personId: string; engagementLevel: SalesAgentSummary['engagementLevel']; commissionPlanId?: string; startDate: string }) =>
    api.post<SalesAgentSummary>('/commission/agents', data),
  listPlans: () => api.get<CommissionPlan[]>('/commission/plans'),
  getPlan: (id: string) => api.get<CommissionPlanDetail>(`/commission/plans/${id}`),
  createPlan: (data: {
    name: string;
    base?: string;
    structure?: string;
    flatRate?: string;
    floorRule?: boolean;
    bonusEligible?: boolean;
    payOnCollection?: boolean;
    bands?: CommissionBand[];
    bonusRules?: Omit<BonusRule, 'id'>[];
  }) => api.post<CommissionPlan>('/commission/plans', data),
  createVersion: (
    id: string,
    data: Partial<{
      base: string;
      structure: string;
      flatRate: string;
      floorRule: boolean;
      bonusEligible: boolean;
      payOnCollection: boolean;
      bands: CommissionBand[];
    }>,
  ) => api.post<CommissionPlan>(`/commission/plans/${id}/versions`, data),
  activatePlan: (id: string) =>
    api.post<{ plan: CommissionPlan; archivedPriorVersions: string[] }>(`/commission/plans/${id}/activate`),
  archivePlan: (id: string) => api.post<CommissionPlan>(`/commission/plans/${id}/archive`),
  assignAgentPlanById: (agentId: string, commissionPlanId: string | null) =>
    api.patch<SalesAgentSummary>(`/commission/agents/${agentId}`, { commissionPlanId }),
  listSettlementRuns: (agentId?: string) =>
    api.get<CommissionSettlementRun[]>(`/commission/settlement-runs${agentId ? `?agentId=${agentId}` : ''}`),
  getSettlementRun: (id: string) => api.get<CommissionSettlementRunDetail>(`/commission/settlement-runs/${id}`),
  runSettlement: (data: { agentId: string; periodStart: string; periodEnd: string }) =>
    api.post<{ run: CommissionSettlementRun; heldForCollectionCount: number }>(
      '/commission/settlement-runs',
      data,
    ),
  finalizeSettlementRun: (id: string) =>
    api.post<{ run: CommissionSettlementRun; idempotentReplay: boolean }>(
      `/commission/settlement-runs/${id}/finalize`,
    ),
  discardSettlementRun: (id: string) =>
    api.delete<{ discarded: true; id: string; entriesReleased: number }>(
      `/commission/settlement-runs/${id}`,
    ),
};

export interface PriceQuote {
  amount: string;
  currency: string;
  breakdown?: Array<{ label: string; amount: string }>;
}

export const quotesApi = {
  estimate: (data: {
    rateCardId: string;
    originZoneId: string;
    destinationZoneId: string;
    vehicleTypeId: string;
    stops?: number;
  }) => api.post<PriceQuote>('/quotes/estimate', data),
};

export interface OnboardingStep {
  id: string;
  label: string;
  completed: boolean;
  href?: string;
  essential?: boolean;
}

export const onboardingApi = {
  checklist: () => api.get<OnboardingStep[]>('/onboarding/checklist'),
  sync: () => api.post('/onboarding/sync'),
  dispatchSettingsReview: () => api.get<DispatchSettingsReview>('/onboarding/dispatch-settings'),
  confirmDispatchSettings: () =>
    api.post<DispatchSettingsReview>('/onboarding/dispatch-settings/confirm'),
};

export interface DispatchSettingsReview {
  reviewedAt: string | null;
  reviewedBy: string | null;
  reviewedByName: string | null;
}

export interface CompanyProfile {
  legalName: string;
  tradingName: string;
  tin: string;
  billingAddress: string;
  hubLatitude: number;
  hubLongitude: number;
  timezone: string;
  currency: string;
}

export interface Settings {
  companyName: string;
  timezone: string;
  currency: string;
}

export interface SettingRow {
  key: string;
  domain: string;
  value: unknown;
  valueType: 'string' | 'number' | 'boolean' | 'money' | 'uuid' | 'json';
  updatedAt?: string;
}

export const settingsApi = {
  get: async () => {
    const c = await api.get<CompanyProfile>('/settings/company');
    return {
      companyName: c.legalName,
      timezone: c.timezone,
      currency: c.currency,
    } satisfies Settings;
  },
  getCompany: () => api.get<CompanyProfile>('/settings/company'),
  updateCompany: (data: Partial<CompanyProfile>) =>
    api.put<CompanyProfile>('/settings/company', data),
  update: (data: Partial<Settings>) =>
    api.put<CompanyProfile>('/settings/company', {
      legalName: data.companyName,
    }),
  list: () => api.get<SettingRow[]>('/settings'),
  setValue: (key: string, value: unknown) => api.put<{ key: string; value: unknown }>(`/settings/${key}`, { value }),
};

export interface Role {
  id: string;
  name: string;
  isSystem: boolean;
  permissions: Record<string, boolean>;
}

export const rolesApi = {
  list: () => api.get<Role[]>('/people/roles'),
  assignable: () => api.get<Array<{ id: string; name: string }>>('/people/roles/assignable'),
  updatePermissions: (id: string, permissions: Record<string, boolean>) =>
    api.patch<Role>(`/people/roles/${id}/permissions`, { permissions }),
};

export interface IntegrationStatus {
  provider: string;
  status: string;
  configured: boolean;
  lastVerifiedAt?: string | null;
  secret?: string;
  /** Storage only: where files currently go (no secrets). */
  storage?: {
    mode: 'local' | 's3';
    source: 'credentials' | 'env' | 'local';
    provider?: 'r2' | 's3';
    bucket?: string;
    endpointHost?: string;
    note?: string;
  };
  /** Firebase only: which project push notifications use (no secrets). */
  firebase?: { projectId?: string; clientEmail?: string };
}

export interface XeroFailedPush {
  id: string;
  invoiceRunId?: string | null;
  idempotencyKey: string;
  errorMessage?: string | null;
  attempts: number;
  status: 'pending' | 'resolved';
  lastAttemptAt?: string | null;
  resolvedAt?: string | null;
  createdAt: string;
}

export interface MapsUsageCounter {
  id: string;
  sku: string;
  month: string;
  callCount: number;
}

export interface AddressPrediction {
  placeId: string;
  description: string;
}

export const integrationsApi = {
  list: () => api.get<IntegrationStatus[]>('/integrations'),
  status: (provider: string) =>
    api.get<IntegrationStatus>(`/integrations/${provider}/status`),
  saveCredentials: (provider: string, credentials: Record<string, string>) =>
    api.put<IntegrationStatus>(`/integrations/${provider}/credentials`, { credentials }),
  testEmail: (to: string) =>
    api.post<{ success: boolean; mode: string; message?: string }>('/integrations/resend/test', {
      to,
    }),
  /** @deprecated use testEmail */
  testSmtp: (to: string) =>
    api.post<{ success: boolean; mode: string; message?: string }>('/integrations/resend/test', {
      to,
    }),
  xeroOAuthUrl: () => api.get<{ url: string; mode: string }>('/integrations/xero/oauth-url'),
  testPush: () =>
    api.post<{ success: boolean; configured: boolean; devices: number; sent: number; message: string }>(
      '/integrations/firebase/test',
      {},
    ),
  testStorage: () =>
    api.post<{ success: boolean; mode: 'local' | 's3'; message: string; bucket?: string }>(
      '/integrations/storage/test',
      {},
    ),
  mapsUsage: (month?: string) =>
    api.get<MapsUsageCounter[]>(`/integrations/google/usage${month ? `?month=${month}` : ''}`),
  getMapsProvider: () =>
    api.get<{
      provider: 'google' | 'mapbox' | 'manual';
      styleUrl?: string;
      autocompleteMinChars?: number;
      autocompleteDebounceMs?: number;
      permanentGeocoding?: boolean;
    }>('/integrations/maps/provider'),
  setMapsProvider: (provider: 'google' | 'mapbox' | 'manual') =>
    api.put<{ provider: 'google' | 'mapbox' | 'manual' }>('/integrations/maps/provider', {
      provider,
    }),
  autocomplete: (input: string, sessionToken: string) =>
    api.post<{ predictions: AddressPrediction[]; sessionToken: string; provider?: string }>(
      '/integrations/google/autocomplete',
      { input, sessionToken },
    ),
  geocode: (address: string, addressId?: string) =>
    api.post<{ latitude: number; longitude: number; zoneId: string | null; mode: string }>(
      '/integrations/google/geocode',
      { address, addressId },
    ),
  xeroFailedPushes: () => api.get<XeroFailedPush[]>('/integrations/xero/failed-pushes'),
  xeroRetryFailed: () =>
    api.post<{ attempted: number; resolved: number; stillFailing: number }>(
      '/integrations/xero/retry-failed',
    ),
};

export interface PodDocument {
  id: string;
  tripStopId: string;
  podNumber: string;
  documentType: 'pod' | 'loading_tally';
  generatedAt: string;
  signatoryName?: string | null;
  piecesDelivered: number;
  pdfStorageKey?: string | null;
  signatureImageKey?: string | null;
  signedStatePhotoKey?: string | null;
  immutableHash: string;
  correctsPodId?: string | null;
  createdAt?: string;
  // Legacy/optional fields — not always populated by the backend.
  tripId?: string;
  stopId?: string;
  filename?: string;
  url?: string;
}

export const podsApi = {
  list: (tripId: string) => api.get<PodDocument[]>(`/trips/${tripId}/pods`),
  byStop: (stopId: string) => api.get<PodDocument | null>(`/stops/${stopId}/pod`),
  get: (id: string) => api.get<PodDocument>(`/pod/${id}`),
  generate: (data: {
    tripStopId: string;
    documentType?: 'pod' | 'loading_tally';
    signatoryName: string;
    piecesDelivered: number;
    signatureImageKey?: string;
    signedStatePhotoKey?: string;
  }, opts?: CrewActionOptions) => crewPost<PodDocument>('/pod/generate', { ...data }, opts),
  captureSignature: (
    podId: string,
    data: { signatoryName: string; signatureImageKey: string; signedStatePhotoKey?: string },
    opts?: CrewActionOptions,
  ) => crewPost<PodDocument>(`/pod/${podId}/signature`, { ...data }, opts),
  verify: (id: string) => api.get<{ valid: boolean; stored: string; computed: string }>(`/pod/${id}/verify`),
  /** Save the POD PDF (presigned R2 link or streamed through the API). */
  downloadPdf: (id: string, podNumber?: string | null) =>
    downloadDocument(`/pods/${id}/pdf`, `${podNumber ?? 'POD'}.pdf`),
};

export interface HeldCargoItem {
  id: string;
  originStopId: string;
  clientId: string;
  pieces: number;
  reason: string;
  receivedAt: string;
  releasedAt?: string | null;
  releaseTripId?: string | null;
}

export const heldCargoApi = {
  list: (ageingOnly?: boolean) =>
    api.get<HeldCargoItem[]>(`/pod/held-cargo${ageingOnly ? '?ageingOnly=true' : ''}`),
  register: (data: { originStopId: string; clientId: string; pieces: number; reason: string }) =>
    api.post<HeldCargoItem>('/pod/held-cargo', data),
  release: (id: string, releaseTripId: string) =>
    api.post<HeldCargoItem>(`/pod/held-cargo/${id}/release`, { releaseTripId }),
};

export interface ExceptionTaxonomyEntry {
  type: string;
  defaultBillability: 'billable' | 'non_billable' | 'case_by_case';
  recommendedAction: string;
}

export interface ExceptionRecord {
  id: string;
  tripId: string;
  stopId?: string | null;
  exceptionType: string;
  reasonCode: string;
  state: string;
  photoKeys?: string[];
  causeSide?: string | null;
  defaultBillability?: 'billable' | 'non_billable' | 'case_by_case' | null;
  recommendedAction?: string | null;
  decision?: string | null;
  decisionReason?: string | null;
  pesoEffect?: string | null;
  decidedBy?: string | null;
  decidedAt?: string | null;
  filedAt?: string;
  createdAt?: string;
}

export const exceptionsApi = {
  taxonomy: () => api.get<ExceptionTaxonomyEntry[]>('/exceptions/taxonomy'),
  listForTrip: (tripId: string) => api.get<ExceptionRecord[]>(`/exceptions/trips/${tripId}`),
  file: (data: {
    tripId: string;
    stopId?: string;
    exceptionType: string;
    reasonCode: string;
    photoKeys?: string[];
    causeSide?: string;
  }, opts?: CrewActionOptions) => crewPost<ExceptionRecord>('/exceptions', { ...data }, opts),
  approvalQueue: () => api.get<ExceptionRecord[]>('/exceptions/approval-queue'),
  submit: (id: string) => api.post<ExceptionRecord>(`/exceptions/${id}/submit`),
  decide: (
    id: string,
    data: { decision: 'approve' | 'waive' | 'apply'; decisionReason: string; pesoEffect?: string },
  ) => api.post<ExceptionRecord>(`/exceptions/${id}/decide`, data),
};

export interface FuelPrice {
  id: string;
  pricePerLitre: string;
  effectiveFrom: string;
  effectiveTo?: string | null;
  notes?: string | null;
}

export const fuelPricesApi = {
  list: () => api.get<FuelPrice[]>('/fleet/fuel-prices'),
  create: (data: { pricePerLitre: string; effectiveFrom: string; notes?: string }) =>
    api.post<FuelPrice>('/fleet/fuel-prices', data),
};

export type NotificationTier = 'act_now' | 'awareness' | 'digest';

export interface NotificationTemplate {
  id: string;
  type: string;
  subjectTemplate: string;
  bodyTemplate: string;
  defaultTier: NotificationTier;
  updatedAt?: string;
}

export interface NotificationRecord {
  id: string;
  recipientUserId: string;
  recipientEmail?: string;
  tier: NotificationTier;
  category: string;
  subject: string;
  body: string;
  channelsAttempted?: string[];
  deliveryResults?: Record<string, string>;
  readAt?: string | null;
  deepLink?: string | null;
  createdAt: string;
}

export interface NotificationPreferences {
  channelPreferences: Record<string, string[]>;
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
  notificationTimezone: string;
}

export interface HardFloor {
  id: string;
  tier: NotificationTier;
  category: string;
  channels: string[];
  quietHoursPolicy: string;
  description?: string | null;
  updatedAt?: string;
}

export const notificationsApi = {
  list: (unreadOnly?: boolean) =>
    api.get<NotificationRecord[]>(`/notifications${unreadOnly ? '?unreadOnly=true' : ''}`),
  markRead: (id: string) => api.post<NotificationRecord>(`/notifications/${id}/read`),
  markAllRead: () => api.post<{ ok: boolean }>('/notifications/read-all'),
  templates: () => api.get<NotificationTemplate[]>('/notifications/templates'),
  saveTemplate: (data: {
    type: string;
    subjectTemplate: string;
    bodyTemplate: string;
    defaultTier: NotificationTier;
  }) => api.put<NotificationTemplate>('/notifications/templates', data),
  deliveryLog: (limit?: number) =>
    api.get<NotificationRecord[]>(`/notifications/delivery-log${limit ? `?limit=${limit}` : ''}`),
  getPreferences: () => api.get<NotificationPreferences>('/notifications/preferences'),
  updatePreferences: (data: Partial<NotificationPreferences>) =>
    api.patch<NotificationPreferences>('/notifications/preferences', data),
  hardFloors: () => api.get<HardFloor[]>('/notifications/hard-floors'),
  saveHardFloor: (data: {
    tier: NotificationTier;
    category: string;
    channels: string[];
    quietHoursPolicy?: 'defer_interruptive' | 'always_deliver';
    description?: string;
  }) => api.put<HardFloor>('/notifications/hard-floors', data),
  qaProbe: (data: {
    tier?: NotificationTier;
    category: string;
    subject?: string;
    body?: string;
  }) =>
    api.post<{
      notification: NotificationRecord;
      diagnostics: {
        hardFloorMatched: boolean;
        hardFloor: { id: string; channels: string[]; quietHoursPolicy: string } | null;
        recipientOptOutChannels: string[];
        resolvedChannels: string[];
        inQuietHours: boolean;
        deferQuietHours: boolean;
        timezone: string;
        quietHoursStart: string | null;
        quietHoursEnd: string | null;
        forceInAppOnly: true;
        externalSendAttempted: false;
      };
    }>('/notifications/qa-probe', data),
};

// ─── Live GPS (crew Android app) ────────────────────────────────────────────

export interface LivePosition {
  vehicleId: string | null;
  plateNumber: string | null;
  personId: string;
  driverName: string | null;
  tripId: string | null;
  tripNumber: string | null;
  tripStatus: string | null;
  vehicleRunId: string | null;
  latitude: number;
  longitude: number;
  accuracyM: number | null;
  speedMps: number | null;
  headingDeg: number | null;
  batteryPct: number | null;
  recordedAt: string;
  receivedAt: string;
  ageSeconds: number;
  stale: boolean;
}

export interface LivePositionsResponse {
  generatedAt: string;
  trackingEnabled: boolean;
  intervalSeconds: number;
  staleAfterSeconds: number;
  positions: LivePosition[];
}

export interface TrackPoint {
  latitude: number;
  longitude: number;
  recordedAt: string;
  accuracyM: number | null;
  speedMps: number | null;
  headingDeg: number | null;
}

export const trackingApi = {
  livePositions: () => api.get<LivePositionsResponse>('/dispatch/live-positions'),
  tripTrack: (tripId: string) =>
    api.get<{ tripId: string; truncated: boolean; points: TrackPoint[] }>(`/trips/${tripId}/track`),
};

// ─── Crew app releases ──────────────────────────────────────────────────────

export interface AppRelease {
  id: string;
  platform: string;
  versionCode: number;
  versionName: string;
  notes: string | null;
  mandatory: boolean;
  minSupportedVersionCode: number | null;
  sizeBytes: number;
  sha256: string;
  createdAt: string;
  publishedAt: string | null;
  withdrawnAt: string | null;
  status: 'draft' | 'published' | 'withdrawn';
  downloadPath: string | null;
}

export interface LatestAppRelease {
  versionCode: number;
  versionName: string;
  notes: string | null;
  mandatory: boolean;
  minSupportedVersionCode: number | null;
  sizeBytes: number;
  sha256: string;
  publishedAt: string | null;
  downloadUrl: string;
}

export interface AppReleaseUpload {
  file: File;
  versionCode: number;
  versionName: string;
  notes?: string;
  mandatory: boolean;
  minSupportedVersionCode?: number | null;
}

export const appReleasesApi = {
  list: () => api.get<AppRelease[]>('/app-releases'),
  publish: (id: string) => api.post<AppRelease>(`/app-releases/${id}/publish`, {}),
  withdraw: (id: string) => api.post<AppRelease>(`/app-releases/${id}/withdraw`, {}),
  remove: (id: string) => api.delete<{ ok: true }>(`/app-releases/${id}`),
  /** Public: newest published version, or null when none is published yet. */
  latest: async (): Promise<LatestAppRelease | null> => {
    try {
      return await apiRequest<LatestAppRelease>('/app/android/latest', { skipAuth: true });
    } catch (err) {
      if ((err as ApiError).status === 404) return null;
      throw err;
    }
  },
  /**
   * Multipart upload with progress (fetch has no upload progress). Large APKs can take a
   * while on office connections, so there is no short timeout here.
   */
  upload: (data: AppReleaseUpload, onProgress?: (fraction: number) => void): Promise<AppRelease> =>
    new Promise((resolve, reject) => {
      const form = new FormData();
      // Text fields first, file last (the server reads them in order).
      form.append('versionCode', String(data.versionCode));
      form.append('versionName', data.versionName);
      form.append('notes', data.notes ?? '');
      form.append('mandatory', data.mandatory ? 'true' : 'false');
      if (data.minSupportedVersionCode != null) {
        form.append('minSupportedVersionCode', String(data.minSupportedVersionCode));
      }
      form.append('file', data.file, data.file.name);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/api/app-releases');
      xhr.withCredentials = true;
      const token = getToken();
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
      };
      xhr.onerror = () => reject({ message: 'Network error — the upload did not finish.', status: 0 } as ApiError);
      xhr.onload = () => {
        let body: { message?: string } & Partial<AppRelease> = {};
        try {
          body = JSON.parse(xhr.responseText || '{}');
        } catch {
          /* ignore */
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(body as AppRelease);
        } else if (xhr.status === 413) {
          reject({ message: 'The APK is larger than 150 MB.', status: 413 } as ApiError);
        } else {
          reject({ message: body.message ?? `Upload failed (${xhr.status})`, status: xhr.status } as ApiError);
        }
      };
      xhr.send(form);
    }),
};
